import React, {useRef, useEffect, useCallback, useState} from 'react';
import { observer } from 'mobx-react-lite';
import {reaction, IReactionDisposer, set} from 'mobx';
import { InteractiveElement } from '../../utils/InteractiveElement';
import { CompositionStore } from '../../../../store/CompositionStore';
import { WindowStore } from '../../../../store/WindowStore';
import {Clip, ClipTypeEnum, Engine, EventsEnum, TextClip} from '@rendley/sdk';
import { ApplicationStore } from '../../../../store/ApplicationStore';
import CanvasGuidelinesManager from "../../managers/CanvasGuidelinesManager";
import canvasGuidelinesManager from "../../managers/CanvasGuidelinesManager";
import CanvasAlignmentGuidelinesContainer
  from "../CanvasAlignmentGuidelinesContainer/CanvasAlignmentGuidelinesContainer";

const DragResizeRotateContainer: React.FC = observer(() => {
  const containerRef = useRef<HTMLDivElement>(null);
  const interactionRef = useRef<InteractiveElement | null>(null);
  const pixelRatioRef = useRef<number>(1);
  const [selectedLibrettoClipId, setSelectedLibrettoClipId] = useState(ApplicationStore.selectedLibrettoClipId);

  const dragOffsetRef = useRef([]);
  const canvasGuidelinesContainerRef = useRef<HTMLDivElement>(null);

  const canvasGuidelinesManagerRef = useRef(new CanvasGuidelinesManager());

  const getSelectedClip = useCallback(() => {
    if (ApplicationStore.selectedLibrettoClipId == null) {
      return null;
    }
    const clipIds = CompositionStore.getClipIdsForLibrettoClip(ApplicationStore.selectedLibrettoClipId);
    if (clipIds.length > 0) {
      const sampleClipId = clipIds[0];
      const clip = Engine.getInstance().getClipById(sampleClipId);
      if (!clip?.hasSprite() && clip?.getType() !== ClipTypeEnum.SUBTITLES) {
        return null;
      }
      return clip;
    }
    return null;
  }, []);

  const getLibrettoClipData = useCallback(() => {
    if (ApplicationStore.selectedLibrettoClipId == null) return null;

    const clipIds = CompositionStore.getClipIdsForLibrettoClip(ApplicationStore.selectedLibrettoClipId);
    const clips = clipIds.map(id => Engine.getInstance().getClipById(id)).filter(Boolean);

    return {
      clipIds,
      clips,
      primaryClip: clips[0], // For reading position/style
      hasSprite: clips.some(clip => clip?.hasSprite()),
      isSubtitles: clips.some(clip => clip?.getType() === ClipTypeEnum.SUBTITLES)
    };
  }, []);

  const handleDrag = (x: number, y: number) => {
    const librettoClip = getLibrettoClipData();
    if (!librettoClip?.primaryClip) return;

    if (librettoClip.isSubtitles) {
      // Handle subtitle dragging using SubtitlesManager
      const sprite = Engine.getInstance().getSubtitlesManager().getMainTextContainer();
      let width = sprite.width;
      if (sprite.style.wordWrap && sprite.style.wordWrapWidth > 0) {
        width = sprite.style.wordWrapWidth * sprite.scale.x;
      }
      const height = sprite.height;

      let newX = x / pixelRatioRef.current + dragOffsetRef.current[0];
      let newY = y / pixelRatioRef.current + dragOffsetRef.current[1];

      // Apply guidelines logic for subtitles
      const vGuide = canvasGuidelinesManagerRef.current.getVerticalGuideline(newX, width);
      const hGuide = canvasGuidelinesManagerRef.current.getHorizontalGuideline(newY, height);

      // @ts-ignore
      canvasGuidelinesContainerRef.current?.SetVerticalGuidelines(vGuide != null ? [vGuide * pixelRatioRef.current] : []);

      // @ts-ignore
      canvasGuidelinesContainerRef.current?.SetHorizontalGuidelines(hGuide != null ? [hGuide * pixelRatioRef.current] : []);

      if (vGuide != null) {
        newX = canvasGuidelinesManagerRef.current.applyGuidelineSnapping(newX, width, vGuide);
      }

      if (hGuide != null) {
        newY = canvasGuidelinesManagerRef.current.applyGuidelineSnapping(newY, height, hGuide);
      }

      // Apply position to subtitles
      Engine.getInstance().getSubtitlesManager().setPosition(newX, newY);
    } else {
      // Handle regular clip dragging
      // Calculate new position from primary clip
      const width = librettoClip.primaryClip.style.getWidth();
      const height = librettoClip.primaryClip.style.getHeight();
      let newX = x / pixelRatioRef.current + width / 2;
      let newY = y / pixelRatioRef.current + height / 2;

      // Apply guidelines logic...
      const vGuide = canvasGuidelinesManagerRef.current.getVerticalGuideline(newX, width);
      const hGuide = canvasGuidelinesManagerRef.current.getHorizontalGuideline(newY, height);

      // @ts-ignore
      canvasGuidelinesContainerRef.current?.SetVerticalGuidelines(vGuide != null ? [vGuide * pixelRatioRef.current] : []);

      // @ts-ignore
      canvasGuidelinesContainerRef.current?.SetHorizontalGuidelines(hGuide != null ? [hGuide * pixelRatioRef.current] : []);

      if (vGuide != null) {
        newX = canvasGuidelinesManagerRef.current.applyGuidelineSnapping(newX, width, vGuide);
      }

      if (hGuide != null) {
        newY = canvasGuidelinesManagerRef.current.applyGuidelineSnapping(newY, height, hGuide);
      }

      // Apply to ALL clips in the LibrettoClip
      librettoClip.clips.forEach(clip => {
        if (clip?.hasSprite()) {
          clip.style.setPosition(newX, newY);
        }
      });
    }
  };

  const handleScale = (deltaWidth: number, deltaHeight: number) => {
    const librettoClip = getLibrettoClipData();
    if (!librettoClip?.primaryClip) return;

    if (librettoClip.isSubtitles) {
      // Handle subtitle scaling using SubtitlesManager
      handleScaleSubtitles(deltaWidth, deltaHeight);
    } else {
      // Handle regular clip scaling
      // Calculate scale from primary clip
      const initialWidth = librettoClip.primaryClip.style.getWidth();
      const initialHeight = librettoClip.primaryClip.style.getHeight();
      const [initialScaleX, initialScaleY] = librettoClip.primaryClip.style.getScale();

      // Calculate new scale...
      const newDeltaWidth = deltaWidth / pixelRatioRef.current;
      const newDeltaHeight = deltaHeight / pixelRatioRef.current;

      let scaleX = ((initialWidth + newDeltaWidth) * initialScaleX) / initialWidth;
      let scaleY = ((initialHeight + newDeltaHeight) * initialScaleY) / initialHeight;

      // Clamp the values
      if (initialScaleX >= 0) scaleX = Math.max(0.001, scaleX);
      else scaleX = Math.min(-0.001, scaleX);

      if (initialScaleY >= 0) scaleY = Math.max(0.001, scaleY);
      else scaleY = Math.min(-0.001, scaleY);

      // Apply to ALL clips
      librettoClip.clips.forEach(clip => {
        if (clip?.hasSprite()) {
          clip.style.setScale(scaleX, scaleY);
        }
      });
    }
  };

  // Store initial dimensions at the start of scaling
  const initialSubtitleStateRef = useRef<{scale: number, width: number, height: number} | null>(null);

  const handleScaleSubtitles = (deltaWidth: number, deltaHeight: number) => {

  };

  const handleChangeTextWarp = (deltaWidth: number) => {
    const librettoClip = getLibrettoClipData();
    if (!librettoClip || librettoClip.primaryClip.getType() !== 'text') return;

    librettoClip.clips.forEach(clip => {
      if (clip?.getType() === 'text') {
        const textClip = clip as TextClip;

        let initialWidth = 0;
        const wordWrapWidth = textClip.style.getWordWrapWidth();
        if (wordWrapWidth !== null) {
          initialWidth = wordWrapWidth;
        } else {
          initialWidth = textClip.style.getWidth() - textClip.style.getPadding()[0];
        }

        const width = Math.max(0, initialWidth + deltaWidth * 2);
        textClip.style.setWordWrapWidth(width);
      }
    });
  };

  const handleResize = (deltaWidth: number, deltaHeight: number) => {
    const selectedClip = getSelectedClip();
    if (selectedClip == null) return;

    if (selectedClip.getType() === 'text') {
      return handleChangeTextWarp(deltaWidth);
    }

    if (selectedClip.getType() === ClipTypeEnum.SUBTITLES) {
      return handleSubtitlesTextWrap(deltaWidth);
    }

    return handleScale(deltaWidth, deltaHeight);
  };

  const handleSubtitlesTextWrap = (deltaWidth: number) => {
    const selectedClip = getSelectedClip();

    if (selectedClip == null) {
      return;
    }

    const subtitlesManager = Engine.getInstance().getSubtitlesManager();
    const sprite = subtitlesManager.getMainTextContainer();

    let initialWidth = sprite.width;
    if (sprite.style.wordWrap) {
      initialWidth = sprite.style.wordWrapWidth * sprite.scale.x;
    }

    const width = Math.max(0, initialWidth + deltaWidth / pixelRatioRef.current);
    // sprite.style.wordWrap = true;
    // sprite.style.wordWrapWidth = width / sprite.scale.x;

    subtitlesManager.setMainTextStyle({ wordWrap: true, wordWrapWidth: width / sprite.scale.x });

  };


  const handleRotate = (rotation: number) => {
    const librettoClip = getLibrettoClipData();
    if (!librettoClip) return;

    // Apply rotation to ALL clips
    librettoClip.clips.forEach(clip => {
      if (clip?.hasSprite()) {
        clip.style.setRotation(rotation);
      }
    });
  };

  const handleUpdate = () => {
    const selectedClip = getSelectedClip();

    if (selectedClip == null) return;

    if (selectedClip.getType() === ClipTypeEnum.SUBTITLES) {
      handleUpdateSubtitles();
    }

    if (!selectedClip?.hasSprite()) return;

    const style = selectedClip.style;

    const width = style.getWidth() * pixelRatioRef.current;
    const height = style.getHeight() * pixelRatioRef.current;
    const x = style.getPosition()[0] * pixelRatioRef.current;
    const y = style.getPosition()[1] * pixelRatioRef.current;
    const rotation = style.getRotation();
    const zIndex = style.getZIndex();

    const newX = x - width / 2;
    const newY = y - height / 2;

    interactionRef?.current?.updateStyle({
      width: `${width}px`,
      height: `${height}px`,
      position: 'absolute',
      top: '0px',
      left: '0px',
      transform: `translate(${newX}px, ${newY}px) rotate(${rotation}rad)`,
      transformOrigin: '50% 50%',
      zIndex: `${zIndex}`,
    });
  };

  const handleDragStart = () => {
    // Trigger a undo position
    const librettoClip = getLibrettoClipData();
    if (!librettoClip?.primaryClip) return;

    canvasGuidelinesManagerRef.current.prepareGuidelines();

    if (librettoClip.isSubtitles) {
      // trigger Undo
      const position = Engine.getInstance().getSubtitlesManager().getPosition();
      Engine.getInstance().getSubtitlesManager().setPosition(position[0], position[1]);

      // save offset of dragging
      const sprite = Engine.getInstance().getSubtitlesManager().getMainTextContainer();
      let width = sprite.width;
      if (sprite.style.wordWrap && sprite.style.wordWrapWidth > 0) {
        width = sprite.style.wordWrapWidth * sprite.scale.x;
      }
      dragOffsetRef.current = [position[0] - (sprite.x - width / 2), position[1] - (sprite.y - sprite.height)];
    } else {
      librettoClip.clips.forEach(clip => {
        if (clip?.hasSprite()) {
          const position = clip.style.getPosition();
          clip.style.setPosition(position[0], position[1]);
        }
      });
      dragOffsetRef.current = [0, 0];
    }

    Engine.getInstance().getUndoManager().pushPause(); // We'll pause any per frame updates
  };

  const handleDragEnd = () => {
    canvasGuidelinesManagerRef.current.resetGuidelines();

    // @ts-ignore
    canvasGuidelinesContainerRef.current?.ResetGuidelines();

    Engine.getInstance().getUndoManager().popPause();
  };

const handleUpdateSubtitles =() => {
    const selectedClip = getSelectedClip();

    const sprite = Engine.getInstance().getSubtitlesManager().getMainTextContainer();

    let width = sprite.width * pixelRatioRef.current;
    if (sprite.style.wordWrap && sprite.style.wordWrapWidth > 0) {
      width = sprite.style.wordWrapWidth * sprite.scale.x * pixelRatioRef.current;
    }

    const height = sprite.height * pixelRatioRef.current;
    const x = sprite.x * pixelRatioRef.current - width / 2;
    const y = sprite.y * pixelRatioRef.current - height;
    const rotation = sprite.rotation;
    const zIndex = sprite.zIndex;

    return interactionRef.current?.updateStyle({
      width: `${width}px`,
      height: `${height}px`,
      position: "absolute",
      top: `0px`,
      left: `0px`,
      transform: `translate(${x}px, ${y}px) rotate(${rotation}rad)`,
      transformOrigin: "50% 50%",
      zIndex: `${zIndex}`,
    });
  };

  const handleResizeStart = () => {
    handleScaleEnd();
  };

  const handleResizeEnd = () => {
    handleScaleEnd();
  };

  const handleScaleStart = () => {
    const librettoClip = getLibrettoClipData();
    if (!librettoClip?.primaryClip) return;

    // This is for Undo, we'll trigger a set to record scale then pause for the whole manipulation, then resume at the end
    if (librettoClip.isSubtitles) {
      const scale = Engine.getInstance().getSubtitlesManager().getScale();
      Engine.getInstance().getSubtitlesManager().setScale(scale);
      
      // Capture initial state for proper scaling calculations
      const sprite = Engine.getInstance().getSubtitlesManager().getMainTextContainer();
      let width = sprite.width;
      if (sprite.style.wordWrap && sprite.style.wordWrapWidth > 0) {
        width = sprite.style.wordWrapWidth * sprite.scale.x;
      }
      
      initialSubtitleStateRef.current = {
        scale: scale,
        width: width,
        height: sprite.height
      };
    } else {
      const scale = librettoClip.primaryClip.style.getScale();
      librettoClip.clips.forEach(clip => {
        clip.style.setScale(scale[0], scale[1]);
      });
    }

    Engine.getInstance().getUndoManager().pushPause();
  };

  const handleScaleEnd = () => {
    // Clean up initial state to avoid stale data
    initialSubtitleStateRef.current = null;
    
    Engine.getInstance().getUndoManager().popPause();
  };

  const handleRotateStart = () => {
    const librettoClip = getLibrettoClipData();
    if (!librettoClip?.primaryClip) return;

    // Apply rotation start to ALL clips
    librettoClip.clips.forEach(clip => {
        const rotation = clip.style.getRotation();
        clip.style.setRotation(rotation);
    });

    Engine.getInstance().getUndoManager().pushPause();
  };

  const handleRotateEnd = () => {
    Engine.getInstance().getUndoManager().popPause();
  };

  useEffect(() => {
    if (containerRef.current) {
      interactionRef.current = new InteractiveElement(containerRef.current, {
        onDrag: handleDrag,
        onResize: handleResize,
        onScale: handleScale,
        onRotate: handleRotate,
      //  onUpdate: handleUpdate,

        dragStart: handleDragStart,
        dragEnd: handleDragEnd,

        resizeStart: handleResizeStart,
        resizeEnd: handleResizeEnd,

        scaleStart: handleScaleStart,
        scaleEnd: handleScaleEnd,

        rotateStart: handleRotateStart,
        rotateEnd: handleRotateEnd,
      });

      Engine.getInstance().events.on(EventsEnum.SUBTITLES_TRANSFORM_UPDATED, handleUpdate);

      return () => {
        interactionRef.current?.destroy();
        Engine.getInstance().events.off(EventsEnum.SUBTITLES_TRANSFORM_UPDATED, handleUpdate);
      };
    }
  }, [handleDrag, handleResize, handleScale, handleRotate, handleUpdate]);

  useEffect(() => {
    const disposer: IReactionDisposer = reaction(
        () => ({
          currentTime: CompositionStore.currentTime,
          selectedLibrettoClipId: ApplicationStore.selectedLibrettoClipId,
          canvasResolution: WindowStore.canvasResolution,
          videoResolution: {width: CompositionStore.display.width, height: CompositionStore.display.height},
          windowResolution: WindowStore.resolution,
          style: ApplicationStore.selectedLibrettoClipId != null ?
              (() => {
                const clipIds = CompositionStore.getClipIdsForLibrettoClip(ApplicationStore.selectedLibrettoClipId);
                return clipIds.length > 0 ? CompositionStore.styles?.[clipIds[0]] : null;
              })() : null,
          text: ApplicationStore.selectedLibrettoClipId != null ?
              (() => {
                const clipIds = CompositionStore.getClipIdsForLibrettoClip(ApplicationStore.selectedLibrettoClipId);
                return clipIds.length > 0 ? CompositionStore.clips?.[clipIds[0]].text : null;
              })() : null,
        }),
        (payload) => {
          const librettoClip = getLibrettoClipData();
          if (payload.selectedLibrettoClipId == null || !librettoClip) {
            interactionRef.current?.hideHandlers();
            return;
          }

          setSelectedLibrettoClipId(payload.selectedLibrettoClipId);

          // Check if ANY clip in the LibrettoClip is visible
          const isLibrettoClipVisible = librettoClip.clips.some(clip =>
              payload.currentTime >= clip.getLeftBound() &&
              payload.currentTime <= clip.getRightBound()
          );

          // Only show handlers if the LibrettoClip is visible AND has visual elements
          if (isLibrettoClipVisible && (librettoClip.hasSprite || librettoClip.isSubtitles)) {
            // Show handlers based on clip type
            if (librettoClip.isSubtitles) {
              interactionRef.current?.showHandlers({showRotation: false});
            } else {
              interactionRef.current?.showHandlers();
            }
          } else {
            interactionRef.current?.hideHandlers();
            return;
          }

          const [canvasWidth, canvasHeight] = payload.canvasResolution;
          const { width, height } = payload.videoResolution;
          pixelRatioRef.current = Math.min(canvasWidth / width, canvasHeight / height);

          handleUpdate();
        },
        { fireImmediately: true }
    );

    return () => disposer();
  }, [getSelectedClip, handleUpdate]);

  return <>
    <div ref={containerRef} className="absolute"></div>
    <CanvasAlignmentGuidelinesContainer ref={canvasGuidelinesContainerRef}/>
  </>
});

export default DragResizeRotateContainer;